/**
 * FamilyMembers — lets a billing-account owner link and unlink children.
 *
 * Rendered:
 *   1. Embedded at the bottom of BillingPage (discoverable from /billing).
 *   2. Standalone at /family via FamilyPage (see App.tsx).
 *
 * If the user has no billing account, or is not the account owner, a gentle
 * empty state is shown directing them to /billing to start a subscription.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BillingMember } from '../../types/api';
import { getAccount, addMember, removeMember } from '../../api/billing';
import { useAuth } from '../../contexts/AuthContext';

// ─── Badge ────────────────────────────────────────────────────────────────────

const RelationshipBadge: React.FC<{ relationship: string }> = ({
  relationship,
}) => {
  const isOwner = relationship === 'owner';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'capitalize',
        letterSpacing: '0.04em',
        background: isOwner ? '#DCEFCC' : '#FFF3E0',
        color: isOwner ? '#14593C' : '#F58720',
        border: `1.5px solid ${isOwner ? '#14593C33' : '#F5872033'}`,
      }}
    >
      {isOwner ? 'owner' : relationship}
    </span>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const FamilyMembers: React.FC = () => {
  const { user } = useAuth();

  const [members, setMembers] = useState<BillingMember[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  /** null = no account at all */
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);

  const [email, setEmail] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);

  const [removeLoading, setRemoveLoading] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAccount();
      if (!data) {
        setHasAccount(false);
        setIsOwner(false);
        setMembers([]);
        return;
      }
      setHasAccount(true);
      setMembers(data.members ?? []);
      // The caller is the owner if their user_id appears in members with role "owner"
      // OR if there's no owner entry (they own the account but aren't listed).
      const ownerEntry = (data.members ?? []).find(
        (m) => m.relationship === 'owner'
      );
      if (ownerEntry) {
        setIsOwner(ownerEntry.user_id === user?.id);
      } else {
        // No explicit owner entry — assume the authed user is the owner.
        setIsOwner(true);
      }
    } catch {
      setHasAccount(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setAddLoading(true);
    setAddError(null);
    setAddSuccess(null);
    try {
      await addMember(email.trim());
      setAddSuccess(`${email.trim()} added successfully.`);
      setEmail('');
      await load();
    } catch (err: any) {
      // Prefer the server's `error` message from the response body
      const serverMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Could not add member. Please try again.';
      setAddError(serverMsg);
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async (userId: string, name: string) => {
    setRemoveLoading(userId);
    setRemoveError(null);
    try {
      await removeMember(userId);
      await load();
    } catch (err: any) {
      const serverMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        `Could not remove ${name}. Please try again.`;
      setRemoveError(serverMsg);
    } finally {
      setRemoveLoading(null);
    }
  };

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cvr-card" style={{ opacity: 0.45, minHeight: 120 }} />
    );
  }

  // ── No account / not owner — empty state ────────────────────────────────────
  if (!hasAccount || !isOwner) {
    return (
      <div className="cvr-card" style={{ textAlign: 'center', padding: '32px 24px' }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #F58720, #F4C534)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <h3
          style={{
            fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
            fontWeight: 800,
            fontSize: 18,
            color: '#1A2A1F',
            marginBottom: 8,
          }}
        >
          Family management
        </h3>
        <p style={{ color: '#6B7A6F', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          Start a subscription to link and manage your children's accounts from
          one place.
        </p>
        <Link
          to="/billing"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '10px 22px',
            borderRadius: 999,
            background: '#14593C',
            color: 'white',
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          View plans
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    );
  }

  // ── Owner view ──────────────────────────────────────────────────────────────
  const children = members.filter((m) => m.relationship !== 'owner');
  const ownerEntry = members.find((m) => m.relationship === 'owner');

  return (
    <div className="cvr-card">
      <p className="cvr-card__h">Family members</p>

      {/* Member list */}
      <div style={{ display: 'grid', gap: 0 }}>
        {/* Owner row */}
        {ownerEntry && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 0',
              borderBottom: '1px solid var(--line, #EFE6D0)',
            }}
          >
            {/* Avatar */}
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #14593C, #1e7a52)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 15,
                flexShrink: 0,
                fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
              }}
            >
              {ownerEntry.name?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: '#1A2A1F',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ownerEntry.name}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#6B7A6F',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ownerEntry.email}
              </div>
            </div>
            <RelationshipBadge relationship="owner" />
          </div>
        )}

        {/* Child rows */}
        {children.map((member, idx) => (
          <div
            key={member.user_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 0',
              borderBottom:
                idx < children.length - 1
                  ? '1px solid var(--line, #EFE6D0)'
                  : undefined,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #F58720, #F4C534)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 15,
                flexShrink: 0,
                fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
              }}
            >
              {member.name?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: '#1A2A1F',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {member.name}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#6B7A6F',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {member.email}
              </div>
            </div>
            <RelationshipBadge relationship={member.relationship} />
            <button
              className="cvr-btn cvr-btn--danger"
              style={{ padding: '7px 14px', fontSize: 13 }}
              disabled={removeLoading === member.user_id}
              onClick={() => handleRemove(member.user_id, member.name)}
            >
              {removeLoading === member.user_id ? 'Removing…' : 'Remove'}
            </button>
          </div>
        ))}

        {/* Empty children state */}
        {children.length === 0 && (
          <p
            style={{
              fontSize: 14,
              color: '#6B7A6F',
              padding: '14px 0 6px',
              lineHeight: 1.6,
            }}
          >
            No children linked yet. Use the form below to add one.
          </p>
        )}
      </div>

      {/* Remove error */}
      {removeError && (
        <div
          style={{
            marginTop: 12,
            background: '#FAD5C8',
            border: '1.5px solid #E26B4F',
            borderRadius: 10,
            padding: '10px 14px',
            fontSize: 13,
            color: '#8B2E14',
            fontWeight: 600,
          }}
        >
          {removeError}
        </div>
      )}

      {/* Add-child form */}
      <form
        onSubmit={handleAdd}
        style={{ marginTop: 20, display: 'grid', gap: 10 }}
      >
        <label className="cvr-label" htmlFor="family-add-email">
          Add child by email
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            id="family-add-email"
            type="email"
            className="cvr-input"
            placeholder="child@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setAddError(null);
              setAddSuccess(null);
            }}
            disabled={addLoading}
            required
          />
          <button
            type="submit"
            className="cvr-btn"
            disabled={addLoading || !email.trim()}
            style={{ whiteSpace: 'nowrap' }}
          >
            {addLoading ? 'Adding…' : 'Add child'}
          </button>
        </div>

        {addSuccess && (
          <p className="cvr-saved" style={{ marginTop: 2 }}>
            {addSuccess}
          </p>
        )}
        {addError && (
          <div
            style={{
              background: '#FAD5C8',
              border: '1.5px solid #E26B4F',
              borderRadius: 10,
              padding: '10px 14px',
              fontSize: 13,
              color: '#8B2E14',
              fontWeight: 600,
            }}
          >
            {addError}
          </div>
        )}
      </form>
    </div>
  );
};

export default FamilyMembers;
